import { useAppSelector } from 'stores';
import { OrganizationQuery } from 'queries';

export interface OrganizationOption {
  value: number;
  name: string;
}

// 운영 조직(연봉인상) — 이 조직 role 의 권한은 모든 조직에 적용 (백엔드 OPERATOR_ORG_ID 와 동일)
export const OPERATOR_ORG_ID = 1;

// 현재 로그인한 어드민이 특정 permission 을 가진 조직 옵션 목록을 반환.
// 운영 조직 role 로 가졌으면 모든 조직(isGlobal), 아니면 권한 있는 조직만.
// single 은 "옵션이 1개뿐" 인 경우 자동 선택용 값 — 없으면 null.
function useOrganizationOptions(permission: string) {
  const user = useAppSelector((s) => s.auth.user);
  const isGlobal =
    user?.roles.some(
      (r) =>
        r.organizationId === OPERATOR_ORG_ID &&
        r.permissions.includes(permission),
    ) ?? false;
  const { data: allOrgs } = OrganizationQuery.useGetAll(isGlobal);

  if (!user) {
    return {
      options: [] as OrganizationOption[],
      single: null as number | null,
      isGlobal,
    };
  }

  if (isGlobal) {
    const options: OrganizationOption[] = (allOrgs ?? []).map((o) => ({
      value: o.id,
      name: o.displayName,
    }));
    return { options, single: null as number | null, isGlobal };
  }

  const orgMap = new Map<number, string>();
  user.roles.forEach((r) => {
    if (r.permissions.includes(permission)) {
      orgMap.set(r.organizationId, r.organizationName || '');
    }
  });
  const options: OrganizationOption[] = Array.from(orgMap.entries()).map(
    ([id, name]) => ({ value: id, name }),
  );
  const single: number | null = options.length === 1 ? options[0].value : null;
  return { options, single, isGlobal };
}

export default useOrganizationOptions;
